import { SortingState } from "@tanstack/react-table";
import clsx from "clsx";
import { ArrowDownAZ, ArrowDownUp, ArrowUpZA, Download, RefreshCcw } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { ReactComponent as ChevronDownIcon } from "../../../assets/icons/caret-down.svg";
import { ReactComponent as CheckMarkIcon } from "../../../assets/icons/check-mark.svg";
import { ReactComponent as SearchIcon } from "../../../assets/icons/search.svg";
import { BillingFilters as Filters, InvoiceStatus, STATUS_LABELS } from "../types";

const STATUS_OPTIONS: { value: InvoiceStatus | "all"; label: string }[] = [
  { value: "all", label: "All Statuses" },
  { value: "pending", label: "Pending" },
  { value: "invoiced", label: "Invoiced" },
  { value: "paid", label: "Paid" },
  { value: "failed", label: "Failed" },
  { value: "not_configured", label: "Not Configured" },
];

// Column ids must match columns.tsx. "Client" was "hotelName" before — a column
// that does not exist, so the option silently did nothing.
const SORT_OPTIONS: { id: string; label: string }[] = [
  { id: "clientName", label: "Client" },
  { id: "planDescription", label: "Plan" },
  { id: "commissionRate", label: "Rate" },
  { id: "bookingRevenue", label: "Bookings" },
  { id: "commission", label: "Commission" },
  { id: "invoiceStatus", label: "Status" },
];

export type BillingFiltersProps = {
  filters: Filters;
  onFiltersChange: (patch: Partial<Filters>) => void;
  sorting: SortingState;
  onSortingChange: (s: SortingState) => void;
  selectedCount: number;
  onBulkCharge: () => void;
  onExportCsv: () => void;
  isExporting: boolean;
  onSyncAll: () => void;
  isSyncPending: boolean; // reconcileAllMutation.isPending — drives the spinner (animate-spin) and the "Syncing..." label
  isSyncDisabled: boolean; // isSyncPending || showSyncAll — drives disabled
};

export function BillingFilters({
  filters,
  onFiltersChange,
  sorting,
  onSortingChange,
  selectedCount,
  onBulkCharge,
  onExportCsv,
  isExporting,
  onSyncAll,
  isSyncPending,
  isSyncDisabled,
}: BillingFiltersProps) {
  const { searchQuery, statusFilter, showReadyOnly, showTestUsers } = filters;
  const [statusDropdownOpen, setStatusDropdownOpen] = useState(false);
  const [sortDropdownOpen, setSortDropdownOpen] = useState(false);
  const statusDropdownRef = useRef<HTMLDivElement>(null);
  const sortDropdownRef = useRef<HTMLDivElement>(null);

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        statusDropdownRef.current &&
        !statusDropdownRef.current.contains(event.target as Node)
      ) {
        setStatusDropdownOpen(false);
      }
      if (
        sortDropdownRef.current &&
        !sortDropdownRef.current.contains(event.target as Node)
      ) {
        setSortDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  return (
    <div className="flex flex-wrap items-center gap-3 mb-4">
      {/* Search Input */}
      <div className="flex px-3 py-2 border border-[#E9E9EB] bg-surface-primary rounded-lg items-center gap-2 min-w-[240px]">
        <SearchIcon className="w-4 h-4 text-gray-400" />
        <input
          type="text"
          placeholder="Search by hotel, client, or email..."
          className="w-full outline-none text-sm"
          value={searchQuery}
          onChange={(e) => onFiltersChange({ searchQuery: e.target.value })}
        />
      </div>

      {/* Status Filter Dropdown */}
      <div className="relative" ref={statusDropdownRef}>
        <button
          onClick={() => setStatusDropdownOpen(!statusDropdownOpen)}
          className="flex items-center gap-2 px-3 py-2 bg-surface-primary border border-[#E9E9EB] rounded-lg text-sm hover:bg-surface-secondary"
        >
          <span className="text-content-primary">
            Status:{" "}
            {statusFilter === "all" ? "All" : STATUS_LABELS[statusFilter]}
          </span>
          <ChevronDownIcon
            className={clsx(
              "w-3 h-3 transition-transform",
              statusDropdownOpen && "rotate-180",
            )}
          />
        </button>
        {statusDropdownOpen && (
          <div className="absolute top-full left-0 mt-1 bg-surface-primary border border-[#E9E9EB] rounded-lg shadow-lg z-20 min-w-[160px]">
            {STATUS_OPTIONS.map((option) => (
              <button
                key={option.value}
                onClick={() => {
                  onFiltersChange({ statusFilter: option.value });
                  setStatusDropdownOpen(false);
                }}
                className={clsx(
                  "w-full px-3 py-2 text-left text-sm hover:bg-[#f2f1ff] flex items-center justify-between",
                  option.value === statusFilter && "bg-[#f2f1ff]",
                )}
              >
                <span>{option.label}</span>
                {option.value === statusFilter && (
                  <CheckMarkIcon className="w-4 h-4 text-primary" />
                )}
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Sort Dropdown */}
      <div className="relative" ref={sortDropdownRef}>
        <button
          onClick={() => setSortDropdownOpen(!sortDropdownOpen)}
          className="flex items-center gap-2 px-3 py-2 bg-surface-primary border border-[#E9E9EB] rounded-lg text-sm hover:bg-surface-secondary"
        >
          <ArrowDownUp className="w-4 h-4 text-content-tertiary" />
          <span className="text-content-primary">
            {sorting.length > 0
              ? `${
                  SORT_OPTIONS.find((o) => o.id === sorting[0].id)?.label ??
                  sorting[0].id
                } ${sorting[0].desc ? "↓" : "↑"}`
              : "Sort by"}
          </span>
          <ChevronDownIcon
            className={clsx(
              "w-3 h-3 transition-transform",
              sortDropdownOpen && "rotate-180",
            )}
          />
        </button>
        {sortDropdownOpen && (
          <div className="absolute top-full left-0 mt-1 bg-surface-primary border border-[#E9E9EB] rounded-lg shadow-lg z-20 min-w-[180px]">
            {SORT_OPTIONS.map((col) => {
              const isActive = sorting.length > 0 && sorting[0].id === col.id;
              const isDesc = isActive && sorting[0].desc;
              return (
                <button
                  key={col.id}
                  onClick={() => {
                    if (isActive) {
                      // Toggle direction or clear
                      if (isDesc) {
                        onSortingChange([]);
                      } else {
                        onSortingChange([{ id: col.id, desc: true }]);
                      }
                    } else {
                      onSortingChange([{ id: col.id, desc: false }]);
                    }
                    setSortDropdownOpen(false);
                  }}
                  className={clsx(
                    "w-full px-3 py-2 text-left text-sm hover:bg-[#f2f1ff] flex items-center justify-between",
                    isActive && "bg-[#f2f1ff]",
                  )}
                >
                  <span>{col.label}</span>
                  {isActive && (
                    <span className="text-primary">
                      {isDesc ? (
                        <ArrowDownAZ className="w-4 h-4" />
                      ) : (
                        <ArrowUpZA className="w-4 h-4" />
                      )}
                    </span>
                  )}
                </button>
              );
            })}
          </div>
        )}
      </div>

      {/* Ready to Charge Toggle */}
      <button
        onClick={() => onFiltersChange({ showReadyOnly: !showReadyOnly })}
        className={clsx(
          "px-3 py-2 rounded-lg text-sm font-medium border transition-colors",
          showReadyOnly
            ? "bg-black text-white border-black"
            : "bg-surface-primary text-content-primary border-[#E9E9EB] hover:bg-surface-secondary",
        )}
      >
        Ready to Charge
      </button>
      {/* Export CSV */}
      <button
        onClick={onExportCsv}
        disabled={isExporting}
        className="px-3 py-2 rounded-lg text-sm font-medium border transition-colors bg-surface-primary text-content-primary border-[#E9E9EB] hover:bg-surface-secondary flex items-center gap-1.5 disabled:opacity-50 disabled:cursor-not-allowed"
      >
        {isExporting ? (
          <div className="w-4 h-4 border-2 border-gray-300 border-t-gray-600 rounded-full animate-spin" />
        ) : (
          <Download className="w-4 h-4" />
        )}
        {isExporting ? "Exporting..." : "Export CSV"}
      </button>

      {/* Sync with Stripe (bulk reconciliation) */}
      <button
        onClick={onSyncAll}
        disabled={isSyncDisabled}
        className="px-3 py-2 rounded-lg text-sm font-medium border transition-colors bg-surface-primary text-content-primary border-[#E9E9EB] hover:bg-surface-secondary flex items-center gap-1.5 disabled:opacity-50 disabled:cursor-not-allowed"
      >
        <RefreshCcw className={clsx("w-4 h-4", isSyncPending && "animate-spin")} />
        {isSyncPending ? "Syncing..." : "Sync with Stripe"}
      </button>

      {/* Show/Hide Test Accounts */}
      <button
        onClick={() => onFiltersChange({ showTestUsers: !showTestUsers })}
        className={clsx(
          "px-3 py-2 rounded-lg text-sm font-medium border transition-colors",
          showTestUsers
            ? "bg-black text-white border-black"
            : "bg-surface-primary text-content-primary border-[#E9E9EB] hover:bg-surface-secondary",
        )}
      >
        {showTestUsers ? "Hide Test Accounts" : "Show Test Accounts"}
      </button>

      {/* Charge All Selected */}
      {selectedCount > 0 && (
        <button
          onClick={onBulkCharge}
          className="bg-black text-white px-4 py-2 rounded-lg text-sm font-semibold hover:bg-black-hover"
        >
          Charge All Selected ({selectedCount})
        </button>
      )}

      {/* Active filters count */}
      {(searchQuery ||
        statusFilter !== "all" ||
        showReadyOnly ||
        sorting.length > 0) && (
        <button
          onClick={() => {
            onFiltersChange({
              searchQuery: "",
              statusFilter: "all",
              showReadyOnly: false,
            });
            onSortingChange([]);
          }}
          className="text-sm text-content-tertiary hover:text-content-primary underline"
        >
          Clear filters
        </button>
      )}
    </div>
  );
}
